import { getCdnUrl } from "owa-config";
import type { ApplicationSettings } from "./schema/ApplicationSettings";

const defaultSdxCdnHost = ["{0}.mro1cdnstorage.public", "onecdn", "static", "microsoft"].join(".");
const defaultSdxCdnUrl = `https://${defaultSdxCdnHost}/mro1cdnstorage/{1}/sdx/{0}/{2}/{3}_web/manifest_web.xml`;

/**
 * All application settings should be defined here following the ECS configuration structure.
 * Please insert new settings in alphabetical order, keep a blank line between different settings to preserve readability and reduce chance of merge conflict.
 */
export const defaultApplicationSettings: ApplicationSettings = {
    AboutOutlook: { enabled: false },
    
    ActionableMessage: {
        resourceUrl: "https://outlook.office365.com/connectors",
    },
    
    ActionableMessageResource: {
        baseEndpoint: "https://outlook.office365.com",
        authTargetResource: "https://outlook.office365.com",
        apiName: "ActionableMessageAutoDiscovery",
        serverOverride: false,
    },
    
    ActivityFeed: {
        enabled: false,
    },
    
    Addin: {
        devicePermissionAllowList: "",
        isVerboseLogsEnabled: false,
        disableUlsLogging: false,
        sdxCdnUrl: defaultSdxCdnUrl,
        sdxDmsUrl: "https://mrodevicemgr.officeapps.live.com/mrodevicemgrsvc/api/v1/SDXReleaseDataPackages",
    },
    
    AddinIDB: {
        acquisitionsCacheTtlHours: 72,
        acquisitionsCachePartialFailureTtlHours: 24,
        acquisitionsCacheMissingThresholdPercent: 50,
    },
    
    AddisonOutage: {
        hasOutage: false,
        outageMessage: "",
        outageLinkUri: "",
    },
    
    Ads: {
        kvpPageType: "Outlook_Desktop_Web",
    },
    
    AdsRefresh: {
        maxTimedRefreshesPerSession: 3,
    },
    
    AdsSDK: {
        jsCdnVersion: "v1.10.86",
    },
    
    Analytics: {
        collectorUrl: undefined,
        disabledDatapoints: [] as string[],
        throttledDatapoints: [] as string[],
        disableIsCoreDatapoints: [] as string[],
        enableHighCountEventThrottle: false,
        highCountEventThrottleWindow: 15,
        highCountEventThrottleThreshold: 250,
        capturePerfProfiles: [] as {
            name: string;
            expire: number;
        }[],
        maxPerfProfilesPerSession: 25,
        flightControls: {} as {
            [eventType: string]: {
                flight: string;
                rate?: number;
            };
        },
    },
    
    AppHealth: {
        appHealthIntervalInMs: 30 /*minutes*/ * 60 /*seconds*/ * 1000 /*milliseconds*/,
    },
    
    AppStorePrompt: {
        promptPeriod: 90,
    },
    
    AttachmentWell: {
        showContextMenuOpenButtons: true,
    },
    
    augloopService: {
        resourceUrl: "https://augloop.office.com",
    },
    
    B2Service: {
        shadowModeEnabledAPIs: [],
        fallbackModeEnabledAPIs: [],
    },
    
    Bing: {
        baseUrl: "https://www.bing.com",
    },
    
    Bizchat: {
        environmentName: "",
        // maps to HubSDK default value
        ssrAuthenticationTimeoutInMs: undefined,
    },
    
    BizchatOcdiEndpoint: {
        hostName: "m365copilotapp.svc.cloud.microsoft",
        resourceUrl: "https://m365copilotapp.svc.cloud.microsoft",
    },
    
    Bookings: {
        enableListMailboxesFallback: false,
    },
    
    BookingsDomainConfig: {
        domainURL: "https://outlook.office.com",
        oauthResourceURL: "",
    },
    
    BookWithMe: {
        useModelAForLicenseCheck: false,
    },
    
    ManagedEventSettings: {
        MaxManagedEventDurationMinutes: 60,
    },
    
    CAPIv3: {
        isCAPIv3Enabled: false,
    },
    
    CalendarCheckAllowPrivateMeetingScheduling: {
        enabled: false,
    },
    
    CalendarInstructions: {
        optionsCategory: "copilot",
    },
    
    CalendarEventForm: {
        showRightAlignedPrimaryActionButtons: false,
        showToolbarStyledAsRibbon: true,
        showColorfulIcons: true,
        qnaButtonEnabled: false,
        backroomChatButtonEnabled: false,
        everyoneChatButtonEnabled: false,
    },
    
    CalendarMacFonts: {
        enabled: false,
    },
    
    CalendarMeetingApps: {
        meetingPlatform: "Teams",
    },
    
    CalendarMeetingBriefingExperiment: {
        armBFlights: "",
        armCFlights: "OfficeAgent.MeetingBriefing.UseBizChatMeetingPrep:true",
        armDFlights: "OfficeAgent.Outlook.EnableModelRouter:false,OfficeAgent.Outlook.ReasoningModel:gpt-55-reasoning",
        armEFlights: "OfficeAgent.Outlook.EnableModelRouter:false,OfficeAgent.Outlook.ReasoningModel:claude-opus-4-8,OfficeAgent.StripTemperature:true",
        armFFlights: "OfficeAgent.Outlook.EnableModelRouter:false,OfficeAgent.Outlook.ReasoningModel:gpt-56-reasoning",
        armGFlights: "OfficeAgent.Outlook.EnableModelRouter:false,OfficeAgent.Outlook.ReasoningModel:gpt-54-mini",
        armALabelSuffix: "mainline",
        armBLabelSuffix: "profile instructions",
        armCLabelSuffix: "bizchat",
        armDLabelSuffix: "gpt55",
        armELabelSuffix: "opus48",
        armFLabelSuffix: "gpt56",
        armGLabelSuffix: "gpt54mini",
        armAExtraInstructionsLabel: "",
        armBExtraInstructionsLabel: "Profile instructions",
        armCExtraInstructionsLabel: "",
        armDExtraInstructionsLabel: "",
        armEExtraInstructionsLabel: "",
        armFExtraInstructionsLabel: "",
        armGExtraInstructionsLabel: "",
        armAEnabled: true,
        armBEnabled: true,
        armCEnabled: true,
        armDEnabled: true,
        armEEnabled: true,
        armFEnabled: true,
        armGEnabled: true,
    },
    
    CalendarMeetingRecap: {
        // Empty or invalid values keep the existing MCPS behavior. A valid UTC cutoff enables
        // HasRecap gating for non-channel meetings created on or after that time.
        hasRecapCutoffUtc: "",
    },
    
    CalendarOnlineMeetingCachingConfig: {
        maxCacheLimit: 2,
        minCacheThreshold: 1,
    },
    
    CalendarReplyToAttendees: {
        enabled: true,
    },
    
    CalendarRibbonNewEventButton: {
        isDropdownEnabled: true,
    },
    
    CalendarSchedulingPoll: {
        enabled: true,
    },
    
    CalendarSurfaceEventTiles: {
        showDashedBorder: false,
    },
    
    CalendarSync: {
        stalenessThresholdInSeconds: 60,
    },
    
    CalendarTeamsToggle: {
        enabled: false,
        isFeedbackEnabled: false,
        disallowedTeamsRings: [] as string[],
    },
    
    CalendarChannelMeetingScheduling: {
        enabled: false,
        allowedTeamsRings: [] as string[],
        nonMemberBannerLearnMoreUrl: "https://support.microsoft.com/teams/teams-channels/channel-meetings-in-microsoft-teams",
    },
    
    Chapter3AllowList: {
        registrationIds: [],
        sourceContents: [],
    },
    
    Chapter3BlockedList: {
        registrationIds: [],
        sourceContents: [],
    },
    
    CLPAudit: {
        resourceUrl: "https://graph.microsoft.com",
    },
    
    Copilot: {
        attachmentSummarizationEnabled: false,
        elaborateEnabled: false,
        coachEnabled: false,
        suggestedDraftsEnabled: false,
        summarizeEnabled: false,
        explainPlacesReportEnabled: false,
        replyByMeetingEnabled: false,
        meetingPrepSummaryEnabled: false,
        disableEdgeCopilotExtract: false,
        disableInboxShopping: false,
        disableCopilot: false,
        disableAddins: false,
        enableMultiLanguage: false,
        calendarElaborateEnabled: false,
        calendarRewriteEnabled: false,
        timeCopilotChatEnabled: false,
        autoResourceBookingEnabled: false,
        prioritizeEnabled: false,
        prioritizeOptInEnabled: false,
        attendeeColocationEnabled: false,
    },
    
    CopilotAgents: {
        draftAgentVersion: "1.0.0",
        sydneyTrafficType: "Prod",
        newsletterVariant: "feature.isNewslettersContent",
        rewriteVariant: "feature.IsRewrite",
    },
    
    CopilotAIX00: {
        attachmentSummaryEnabled: false,
        draftAssistantEnabled: false,
        prioritizeEnabled: false,
        elaboratePolarisEnabled: false,
    },
    
    CopilotPlusPC: {
        supportedLanguages: [],
        maxSummaryPoints: 5,
    },
    
    CopliotRing: {
        ringName: "Dogfood",
    },
    
    Diagnostics: {
        odsSupportPaneTestVersion: "test1",
        panel: false,
    },
    
    Dictation: {
        shouldAcquireMicViaNavigator: false,
    },
    
    DimeModal: {
        ScriptUrl: "https://content.lifecycle.office.net/dime/v1/dime.js",
    },
    
    DistributionGroups: {
        exchangePortalUrl: "https://go.microsoft.com/fwlink/?linkid=2236662",
    },
    
    EnabledResolvers: {
        Query: {},
        Mutation: {},
        Subscription: {},
    },
    
    EditorSdk: {
        audience: "",
    },
    
    EventifyOutage: {
        hasOutage: false,
        outageLinkUri: "",
    },
    
    EventifySunsetNotice: {
        enabled: false,
        message: "",
        informationLink: "",
        readonlyCutoffUtc: "",
        sunsetCutoffUtc: "",
    },
    
    ExtensibilityPlatformType: {
        isMonarch: false,
    },
    
    Feedback: {
        consumerPrivacyUrl: "https://go.microsoft.com/fwlink/?LinkId=521839",
        enterprisePrivacyUrl: "https://myaccount.microsoft.com/settingsandprivacy/privacy",
    },
    
    FeedbackSdk: {
        iFrameUrlEndpoint: "https://admin.microsoft.com",
        tenantCloudType: "Worldwide",
    },
    
    FetchMosAcquisitionOnBootIntervalOverride: {
        overrideIntervalInSeconds: 0, // 0 means no override
    },
    
    ForceReboot: {
        NativeForceRebootEnabled: false,
        WebForceRebootEnabled: false,
    },
    
    ForwardingWithPopImapOption: {
        enabled: false,
    },
    
    GdprAds: {
        vendorListCdnUrl: `https:${getCdnUrl()}assets/ads/7dttl/vendor-list.json`,
        vendorListCdnV3Url: `https:${getCdnUrl()}assets/ads/7dttl/vendor-list-v3.json`,
    },
    
    Graph: {
        resourceUrl: "https://graph.microsoft.com",
    },
    
    GraphResource: {
        authTargetResource: "https://graph.microsoft.com",
        apiName: "Graph",
        baseEndpoint: "https://graph.microsoft.com/",
        serverOverride: false,
    },
    
    GWSApi: {
        resourceUrl: "https://graph.microsoft.com",
    },
    
    Hotkeys: {
        disableAppModuleCommands: false,
    },
    
    SharingDialogIframeHostUrl: {
        enabled: false,
        iframeHostUrl: "https://www.odwebp.svc.ms/share?smkt=",
    },
    
    Iframes: {
        memoryThreshold: 500 * 1024, // 500MB in KB
        memoryPreloadThreshold: 300 * 1024, // 300MB in KB
        memoryVisibleThreshold: 2 * 1024 * 1024, // 2GB in KB
        memoryPressureThreshold: 80, // 80% memory pressure
        backgroundCheckInterval: 30 * 1000, // 30 seconds
        evictionGraceCount: 2, // Number of background checks to wait before evicting iframe after marked for eviction
    },
    
    // ECS control: https://ecs.skype.com/Substrate/OutlookWebClient/configurations/1606589
    ImmersiveEventTemplateEnablement: {
        allowedTeamsRings: ["ring0"],
    },
    
    InboxShopping: {
        allowedDomainsURL: "https:{0}assets/inboxshopping/v1/allowedDomainsBloom.json",
    },
    
    IntelligentRecap: {
        shouldSwitchHivePayload: false,
        alwaysShowRecapChiclet: false,
        hideAIInsights: false,
        dataFetchScenario: 0,
    },
    
    Jana: {
        peopleNovaPeopleApp: true,
        lpeNova: true,
        progressiveLoadingEnabled: false,
        showMailboxSharedOrDelegate: false,
        supportShareAsVCard: false,
        enableBatchTagging: false,
        enableEmailTypes: false,
        disableModalImportProgressDialog: false,
        importContactsBatchSize: 50,
        /**
         * @todo [WI 818832](https://msfast.visualstudio.com/FAST%20Experiences/_workitems/edit/818832): Flip this to default true
         * and rely on the [service flight](https://ecs.skype.com/Substrate/EXO_OwaServer/configurations/1928173).
         */
        enrichImportContactsResponse: false,
        enableCategoryNavActions: false,
    },
    
    LivePersonaCard: {
        allowUrlOverrides: false,
        thirdPartyCustomProperties: false,
        version: "",
    },
    
    LivePersonaEditor: {
        enableSmimeCertificates: false,
    },
    
    Loki: {
        resourceUrl: "",
        complianceEnvironment: "",
    },
    
    LoopAudience: {
        audience: "",
    },
    
    MeControl: {
        aadRememberedAccountsUrl: "https://login.microsoftonline.com",
    },
    
    ProactiveMeetingBriefingSelection: {
        maxSelectedMeetings: 5,
        minimumContextScore: 30,
    },
    
    MeetingBriefingPreviewSelection: {
        minimumContextScore: 30,
    },
    
    MeetingBriefingGeneration: {
        // Shipped OfficeAgent flights for headless personal-briefing generation — the source of
        // truth for the briefing flights. createBriefingClient forwards them as the baseline
        // `extraFlights` to the Outlook Agent; ECS overrides per ring/cloud with no app redeploy
        // (e.g. to promote a set proven in the AI Labs briefing lab). Callers may append per-arm
        // flights on top. Each entry is one `FlightName:value` pair.
        agentFlights: [
            "OfficeAgent.OfficeAgent.EnableMeetingBriefing:true",
            "OfficeAgent.OfficeAgent.EnableMeetingBriefingEdit:true",
            "OfficeAgent.OfficeAgent.EnableMeetingBriefingRegeneration:true",
            "OfficeAgent.FuseStatePersistence:false",
            "OfficeAgent.Outlook.EnableWorkflowEngine:true",
            "OfficeAgent.Outlook.EnableScheduledPrompts:true",
            "OfficeAgent.Outlook.EnableScheduleConfirmation:true",
            "OfficeAgent.Outlook.DataFlywheel:true",
            "OfficeAgent.Outlook.EnableModelRouter:true",
            "OfficeAgent.EnableInternalJsonRpc:true",
        ],
        disableDebugBundle: false,
    },
    
    MeetingTemplates: {
        isTeamsHub: false,
    },
    
    MeetNow: {
        shouldShowInHeader: true,
        useOwaMailHandler: false,
        useTeamsSDKForJoinMeeting: false,
    },
    
    MessageRecall: {
        resourceUrl: "https://outlook.office.com/messagerecallnetcore/",
    },
    
    MonarchLegacyToggle: {
        delayInSecondsBeforeRenderingFeedback: 2,
    },
    
    MonarchNotifications: {
        enabled: false,
    },
    
    MonarchSettingsColumnsFlyout: {
        enabled: false,
    },
    
    Mos3TitlesResource: {
        authTargetResource: "https://titles.prod.mos.microsoft.com",
        apiName: "MOS3",
        baseEndpoint: "https://titles.prod.mos.microsoft.com",
        serverOverride: false,
    },
    
    Mos3Urls: {
        TitlesServiceUrl: "",
        UnifiedStoreUrl: "https://teams.microsoft.com/metaos-store",
    },
    
    MOS: {
        mos3AppServiceAppsInMail: true,
    },
    
    MsPlaces: {
        insightsWithMockData: [],
        enableInsightsTab: false,
        enableBuildingAnalyticsTab: true,
        spaceManagementTabEnabled: false,
        enableDeskpoolTab: true,
        enableAdminAvailableOptions: true,
        enableAnalyticsLearning: false,
        enableHelpVideos: true,
        enableAnalyticsMessageBar: true,
        placesAnalyticsCentroBaseUrl: "https://admin-ignite.microsoft.com", // This is the default value for SDF & Local environment
        enableHomePageSummaryBar: true,
        enableHomePageSummaryCards: true,
    },
    
    MyOutlook: {
        baseUrl: "",
        apiPath: "/outlookpos",
    },
    
    NativeHostAuth: {
        accountCriticalResourceHosts: [] as string[],
        oneAuthEmergencyFlights: "",
    },
    
    NativeReactions: {
        deviceType: "Owa",
    },
    
    NestedAppAuth: {
        skipBrokerClaimsScenarios: ["Addin"],
    },
    
    NewOutlookPromoDialog: {
        storeUrl: "ms-windows-store://pdp/?ProductId=9NRX63209R7B&mode=mini&cid=owaPromotionDownload",
    },
    
    NewslettersPolicyFallback: {
        newslettersAccessLevel: 1, // 0: Undefined, 1: ReadWrite, 2: ReadOnly, 3: Disabled
        newslettersReactions: 1, // 0: Undefined, 1: DefaultOn, 2: DefaultOff, 3: Disabled
        newslettersShowMore: 1, // 0: Undefined, 1: DefaultOn, 2: DefaultOff, 3: Disabled
    },
    
    Nps: {
        anniversary: 1,
        anniversaryInDays: 360,
        dismissLimit: 3,
        launchDelay: 300000, // 5 minutes
        minimumDaysAfterFirstLogon: 7,
        minimumSessionCount: 5,
        npsSamplingOwaAppRate: 1,
        npsSamplingOwaSuiteRate: 0.1,
        npsSamplingMonarchAppRate: 4,
        npsSamplingMonarchSuiteRate: 1,
        npsSamplingMonarchCopilotRate: 4,
        npsSamplingOwaCopilotRate: 1,
        npsSamplingOwaAppRateV2: 1,
        npsSamplingOwaSuiteRateV2: 0.1,
        npsSamplingMonarchAppRateV2: 4,
        npsDialogHeight: "750px",
        npsDialogWidth: "500px",
        numLastUsedCopilotFeatures: 10,
        copilotScenarios: ["AppChat", "BizChat", "Coach", "SuggestedDrafts", "Summarize"],
        anniversarySamplingRates: [
            {
                anniversayIndex: 0,
                isOfficialNPS: false,
                samplingRateMonarch: 4,
                samplingRateOwa: 1,
                samplingRateOwaSuite: 0.1,
            },
            {
                anniversayIndex: 1,
                isOfficialNPS: true,
                samplingRateMonarch: 4,
                samplingRateOwa: 1,
                samplingRateOwaSuite: 0.1,
            },
        ],
    },
    
    OcdiEndpoint: {
        onedriveEndpoint: "onedrive.cloud.microsoft",
    },
    
    OcdiRedirection: {
        cookieExpireDays: 30,
        cookieSetDomains: [
            "outlook-sdf.office.com",
            "outlook-sdf.office365.com",
            "outlook.office.com",
            "outlook.office365.com",
        ],
    },
    
    OcpsPolicyResource: {
        authTargetResource: "https://clients.config.office.net",
        apiName: "OwaOcpsPolicy",
        baseEndpoint: "https://clients.config.office.net/user/v1.0/web/policies",
        serverOverride: false,
    },
    
    OcpsPolicyStore: {
        getOcpsPoliciesEndpoint: "https://clients.config.office.net/user/v1.0/web/policies",
        tokenResourceUrl: "https://clients.config.office.net",
        defaultPolicies: "{\"contactSupportEnabled\": \"1\",\"diagnosticsTroubleshootingEnabled\": \"1\",\"getDiagnosticsEnabled\": \"1\",\"emailCollectionEnabled\": \"1\",\"emailCollectionDefault\": \"0\",\"feedbackEnabled\": \"1\",\"logContentEnabled\": \"1\",\"contentSamplesDefault\": \"1\",\"npsSurveyEnabled\": \"1\",\"optionalConnectedExperiencesEnabled\": \"1\",\"connectedOfficeExperiencesEnabled\": \"1\",\"copilotBlockAccessToOpenFilesEnabled\": \"0\",\"copilotChatSidepaneEnabled\": \"1\",\"outlookRecommendationEnabled\": \"1\",\"screenshotEnabled\": \"1\",\"screenshotDefault\": \"0\",\"loopEnabled\": \"1\",\"loopEnabledForOutlook\": \"1\",\"disableSharingPermissions\": \"0\",\"experiencesDownloadingContentEnabled\": \"1\",\"experiencesAnalyzingContentEnabled\": \"1\",\"expirationDate\": \"\",\"M365LinksHandlingBrowserChoice\": \"\",\"loopPollsEnabled\": \"1\",\"showCardLoops\": \"1\",\"loopPlannerEnabled\": \"1\",\"loopVideoPlaybackEnabled\": \"1\",\"loopVideoRecordEnabled\": \"1\"}",
    },
    
    OneDriveReferral: {
        disableNudgeMessageInAttachmentWell: false,
    },
    
    OnePersonView: {
        enabled: false,
        enhancedContextualization: true,
    },
    
    OutlookServiceUrl: {
        outlookServiceDomain: "",
    },
    
    owasmime: {
        cdnContainer: "owasmime/",
    },
    
    OwaVanityDomains: {
        enabled: false,
    },
    
    OwsApiRetryStrategy: {
        authErrorRetryDelayInMs: 10,
        defaultRetryDelayInMs: 100,
        networkErrorRetryDelayInMs: 5000,
        transientErrorRetryDelayInMs: 500,
        throttledErrorRetryDelayInMs: 2000,
    },
    
    OwsCircuitBreaker: {
        failureThreshold: 5,
        slidingWindowMs: 30000,
        cooldownMs: 60000,
        maxCooldownMs: 180000,
        maxOpenDurationMs: 900000,
        trackedStatusCodes: [502, 503, 504],
        abortRateThreshold: 0.9,
        abortMinSampleSize: 5,
        abortMinDistinctActions: 3,
    },
    
    OwsApiRequestQueue: {
        defaultMaxQueueSize: 5,
        enabled: true,
    },
    
    PDV: {
        peoOfflineTemplate: true,
        peoOfflineDisablePrefetch: true,
        peoOfflineLokiShadow: true,
        peoSubstrateSearch: true,
        peoSubstringMatchSearch: true,
        peoFullPgalRecordSearch: true,
        peoRefreshTokenOnLpcHoverTargetMount: true,
        peoUsePgsDeduplication: false,
        peoUsePgsForContactWrites: false,
        // If issues arise, disable via an ECS `PDV.peoEnrichRelatedPeople=false` config (PR 258176, ICM 842241265)
        peoEnrichRelatedPeople: true,
        peoAllowGracefulErrorHandling: true,
    },
    
    PersonalSkillDelivery: {
        // Shipped OfficeAgent flights for personal (user-authored OneDrive) skill delivery — the
        // source of truth for the personal-skill flights. The Outlook Agent compat client forwards
        // them as `extraFlights` only when a caller actually requests personal skills, so sessions
        // that don't use the feature are unaffected. ECS overrides per ring/cloud with no app
        // redeploy. Each entry is one `FlightName:value` pair.
        //
        // Every one of these is a SILENT no-op when missing — nothing errors, the skill simply
        // never arrives — so all four are required:
        //  - Extensibility.Enabled  -> AugLoop extensibility lifecycle (queryBeforeTurn)
        //  - ExtensibilityEnabled   -> container `capability/upload` handler
        //  - EnableUserSkillAuthor  -> whether AugLoop instantiates the SKILL acquirer at all
        //                              (`isAcquirerEnabled('skill')`, "default false: no flight,
        //                              no acquirer"). Without it the requested skill ids are never
        //                              iterated, which reads as `atMentionAttempted:0` with no error.
        //  - EnableCustomSkills     -> custom-skills surface that loads the delivered package
        //
        // The full `Microsoft.Office.Augloop.OfficeAgent.` spine is used so the same strings also
        // work on desktop hosts, which only forward flights whose wire name contains the
        // case-insensitive `augloop` token.
        agentFlights: [
            "Microsoft.Office.Augloop.OfficeAgent.Extensibility.Enabled:true",
            "Microsoft.Office.Augloop.OfficeAgent.ExtensibilityEnabled:true",
            "Microsoft.Office.Augloop.OfficeAgent.EnableUserSkillAuthor:true",
            "Microsoft.Office.Augloop.OfficeAgent.EnableCustomSkills:true",
        ],
    },
    
    PersonaOffline: {
        resyncIntervalMS: 24 /*hours*/ * 60 /*minutes*/ * 60 /*seconds*/ * 1000 /*milliseconds*/,
        pgalSyncDelayInMilliSeconds: 60 * 1000 /* = 60 seconds */,
        pgalModuleSyncPageSize: 250,
        contactsModuleSyncPageSize: 1000,
        maxCountToSync: 30000, // TODO @kamcheung -same setting as Teams. Honour when https://msfast.visualstudio.com/FAST/_workitems/edit/560707 is implemented
        allowGracefulOutdatedStoreHandling: true,
    },
    
    PlacesAnalyticsHelpVideosUrls: {
        ViewBuildingAnalyticsVideoUrl: "https://www.microsoft.com/en-us/videoplayer-nocookie/embed/RW1lhw3?pid=ocpVideo1&maskLevel=20&market=en-us",
        SwitchLocationsVideoUrl: "https://www.microsoft.com/en-us/videoplayer-nocookie/embed/RW1leYm?pid=ocpVideo3&maskLevel=20&market=en-us",
        ChangeDateRangeVideoUrl: "https://www.microsoft.com/en-us/videoplayer-nocookie/embed/RW1lhw8?pid=ocpVideo2&maskLevel=20&market=en-us",
        ApplyFiltersVideoUrl: "",
        ConfigureWorkDaysVideoUrl: "https://www.microsoft.com/en-us/videoplayer-nocookie/embed/RW1lhwt?pid=ocpVideo5&maskLevel=20&market=en-us",
        ExplainDataUsingCopilotVideoUrl: "",
        ShareReportsVideoUrl: "https://www.microsoft.com/en-us/videoplayer-nocookie/embed/RW1ljLf?pid=ocpVideo4&maskLevel=20&market=en-us",
    },
    
    PlacesOutage: {
        hasOutage: false,
        outageLinkUri: "",
        outageEnumIdMessage: 0,
        outageCustomMessage: "",
        typeMessageVariant: 0,
    },
    
    PlatformType: {
        isMonarch: false,
    },
    
    PoisonedBuild: {
        skipBuilds: [],
    },
    
    PreAuthShellResourceUrls: {
        webShellResourceUrl: "https://webshell.suite.office.com",
        graphResourceUrl: "https://graph.microsoft.com",
        officeApiResourceUrl: "https://api.office.net",
    },
    
    PrepareModule: {
        enabled: false,
    },
    
    PresenceResource: {
        authTargetResource: "https://presence.teams.microsoft.com",
        apiName: "presence",
        baseEndpoint: "https://teams.cloud.microsoft/ups/global/",
        serverOverride: false,
        mriPrefix: "8:orgid",
    },
    
    PromptSuggestions: {
        isTeamsScenario: false,
    },
    
    PrivacySettings: {
        enabled: false,
        roamingEnabled: false,
    },
    
    RebootOnSettingsChange: { enabled: false },
    
    RecapDeeplink: {
        phase0Enabled: false,
    },
    
    ReliabilityCheck: {
        isPresent: false,
    },
    
    RsvpSuggestions: {
        highImportanceLowerBound: 8,
        lowImportanceHigherBound: 4,
        serviceSuggestionEnabled: false,
    },
    
    Safelinks: {
        staticPageBaseUrl: "https://outlook.office.com/mail/safelink.html",
        getServiceLocatorHost: "https://na01.safelinks.protection.outlook.com",
        isSupported: true,
    },
    
    SafelinksLite: {
        liteReputationBaseUrl: "https://safelinks.protection.outlook.com",
    },
    
    SafelinksResource: {
        authTargetResource: "https://dataservice.o365filtering.com",
        apiName: "Safelinks",
        baseEndpoint: "https://na01.safelinks.protection.outlook.com/",
        serverOverride: false,
    },
    
    SafelinksURLConfigs: {
        SafelinksHostnames: "safelinks.protection.com,safelinks.protection.office365.us,safelinks.protection.outlook.com,safelinks.protection.outlook.cn,safelinks.protection.outlook.de,safelinks.o365filtering-int.com",
        SpoLofHostnames: "sharepoint-df.com,sharepoint.com,sharepoint.de,sharepoint.cn,sharepoint-mil.us,sharepoint.us",
        GetParams: "url",
        DropParams: "data,sdata,reserved",
        SpoLofDropParams: "xsdata,sdata,ovuser,clickParams",
    },
    
    ScheduleWithCopilot: {
        InsertAgendaOnBodyClickEnabled: true,
    },
    
    SearchAuthResource: {
        enabled: false,
        authResource: "https://outlook.office.com",
    },
    
    SearchInterleaving: {
        gmailLatencyThresholdInMs: 10000,
    },
    
    SearchOfflineFallback: {
        timeoutInMs: 5000,
    },
    
    SettingsUxOutboundLinks: {
        showMobileGetAppLink: true,
        showPopImapSupportLink: true,
        showRulesInfoLink: true,
        showSmimeChromeExtensionLinks: true,
    },
    
    SignalR: {
        LoggingDelay: 20 * 60 * 1000,
        ChannelsToCompareWith: {
            OwaClassicSignalR: ["OwaNetCoreSignalR"],
            OwaNetCoreSignalR: [],
            ONOSSignalR: [],
        },
        MaxConsecutiveStartErrors: 100, // This is the default value, it can be overridden by the SignalrSettings application setting
        ReloadAllThrottleTime: 15000,
        ReconnectDelay: 10000, // How long to wait before trying to reconnect`
        KeepAliveTimeout: 0, // 0 means use server-provided value; in ms when non-zero
        KeepAliveTimeoutWarning: 0, // 0 means use server-provided value; in ms when non-zero
        BeatInterval: 0, // 0 means use computed value; in ms when non-zero
        ReconnectWindow: 0, // 0 means use server-provided value; in ms when non-zero
    },
    
    SignalRSubscription: {
        BatchingIntervalInMs: 500,
        ServiceErrorsForDelayedRetry: [
            "Microsoft.Exchange.Clients.Owa2.Server.Core.OwaServiceCommandConcurrencyLeaseAcquireTimeoutException",
        ],
        MaxRetryAttemptsForQuickRetry: 3,
        MaxRetryAttemptsForDelayedRetry: 8,
        QuickRetryTimeoutInMs: 30000,
    },
    
    SuggestedAttachmentsRecommendationServiceCall: {
        ScenarioName: "OutlookWebMonarch",
    },
    
    SuggestedAttachmentsSuppression: {
        enabled: false,
    },
    
    SupportAndFeedback: {
        policyEnabledIfUndefined: false,
    },
    
    SwCache: {
        successRatio: 1,
        chunkSize: 100,
    },
    
    SydneyEndpoint: {
        cafe: "https://substrate.office.com/m365chat/Chathub",
        cortana: "https://api.cortana.ai/m365chat/SecuredChatHub",
        avalon: "https://substrate.office.com/m365Copilot/Chathub",
        // avalon sdf-pilot: 'https://sdf-s01-00-nam-westus.substrate.cosmic-ppe.office.net/m365Copilotcanary/Chathub'
        // avalon sdf: 'https://substrate.office.com/m365Copilot/Chathub'
    },
    
    TodayClientFreshness: {
        enabled: true,
        monarchReloadEnabled: true,
        maximumDocumentAgeMs: 24 * 60 * 60 * 1000,
        inactivityMs: 30 * 60 * 1000,
        checkIntervalMs: 30 * 60 * 1000,
        reloadCooldownMs: 30 * 60 * 1000,
    },
    
    TeamsArtifactsService: {
        serviceName: "Teams Artifacts",
        serviceBaseUrl: "",
        serviceResourceUrl: "https://teams.cloud.microsoft/6bc3b958-689b-49f5-9006-36d165f30e00",
    },
    
    IC3ActiveMeetingsService: {
        serviceName: "IC3 Active Meetings Service",
        serviceBaseUrl: "",
        serviceResourceUrl: "",
    },
    
    TeamsAuth: {
        serviceName: "Teams Auth Service",
        serviceBaseUrl: "https://teams.microsoft.com/api/authsvc",
        serviceResourceUrl: "https://api.spaces.skype.com",
    },
    
    // ECS control: https://ecs.skype.com/Substrate/OutlookWebClient/configurations/1561226
    TeamsMeetingCopilotMode: {
        ringCopilotModeMapping: [
            { ringId: "ring0", value: "DuringMeeting" },
            { ringId: "ring0_s", value: "DuringMeeting" },
            { ringId: "ring1", value: "DuringMeeting" },
        ],
    },
    
    // ECS control: https://ecs.skype.com/Substrate/OutlookWebClient/configurations/1561226
    TeamsMeetingFacilitatorMode: {
        ringFacilitatorModeMapping: [
            { ringId: "ring0", value: true },
            { ringId: "ring0_s", value: true },
            { ringId: "ring1", value: true },
        ],
        ringFacilitatorDefaultOffIn1to1: [
            { ringId: "ring0", value: true },
            { ringId: "ring0_s", value: true },
            { ringId: "ring1", value: true },
        ],
        ringGroupCopilotAppNameMapping: [
            { ringId: "NonHosted", value: "Facilitator" },
            { ringId: "ring0", value: "Copilot" },
            { ringId: "ring0_s", value: "Copilot" },
            { ringId: "ring1", value: "Copilot" },
        ],
    },
    
    // TODO: Create new ECS configuration for TeamsMeetingSpeechEnabled
    TeamsMeetingSpeechEnabled: {
        ringSpeechEnabledMapping: [{ ringId: "ring0", value: false }],
    },
    
    TeamsMeetingPersonalTemplateLink: {
        resourceUrl: "",
    },
    
    TeamsMeetingUrls: {
        disabled: false,
        allowedCloudDomains: undefined,
    },
    
    TeamsMiddleTier: {
        serviceName: "Teams Middle-Tier",
        serviceBaseUrl: "",
        serviceResourceUrl: "https://api.spaces.skype.com",
    },
    
    TeamsMiddleTierS2S: {
        serviceName: "Teams Middle-Tier S2S",
        serviceBaseUrl: "",
        serviceResourceUrl: "https://api.spaces.skype.com",
    },
    
    TeamsURL: {
        resourceURL: "",
    },
    
    McpsURL: {
        baseURL: "https://teams.microsoft.com/api/mcps/us",
    },
    
    TeamsVirtualEvents: {
        serviceName: "Teams Virtual Events",
        serviceBaseUrl: "",
        serviceResourceUrl: "https://events.teams.cloud.microsoft",
    },
    
    TimeMarkerSuggestionsConfig: {
        enabledSuggestions: [] as string[],
    },
    
    TipsTour: {
        enabled: false,
    },
    
    ToDo: {
        isToDoFeaturesEnabled: true,
    },
    
    Topics: {
        loadSDK: true,
    },
    
    TrouterConfig: {
        TrouterServiceUrl: "https://go.trouter.teams.microsoft.com/",
        RegistrarServiceUrl: "https://teams.microsoft.com/registrar/prod/",
        Ic3Resource: "https://ic3.teams.office.com",
    },
    
    UniversalMeControl: {
        enabled: true,
    },
    
    Upsell: {
        adsFreeUpsellUrl: "https://go.microsoft.com/fwlink/?linkid=2222261",
        adBlockerUpsellUrl: "https://go.microsoft.com/fwlink/?linkid=2222646",
        euAdPromptUrl: "https://go.microsoft.com/fwlink/?linkid=2222558",
        adFreeOnNativeAdUrl: "https://go.microsoft.com/fwlink/?linkid=2192065",
        nativeAdDefaultAdUrl: "https://go.microsoft.com/fwlink/?linkid=2222260",
        usqLeftNavUrl: "https://go.microsoft.com/fwlink/?linkid=2219821",
        usqBizBarUrl: "https://go.microsoft.com/fwlink/?linkid=2219723",
        usqStorageSettingUrl: "https://go.microsoft.com/fwlink/?linkid=2219722",
    },
    
    Urlp: {
        resourceUrl: "https://urlp.asm.skype.com",
        endpointUrl: "https://urlp.asm.skype.com/v1/url/info?url=",
    },
    
    UrlpResource: {
        authTargetResource: "https://urlp.asm.skype.com",
        apiName: "UrlpService",
        baseEndpoint: "https://urlp.asm.skype.com/",
        serverOverride: false,
    },
    
    WebPushNotifications: {
        disabled: false,
    },
    
    Win32SmartCalendar: {
        pastEventsFullIntensity: false,
        brighterColorScheme: false,
    },
    
    XandrSspResource: {
        authTargetResource: "",
        apiName: "XandrSsp",
        baseEndpoint: "https://msft-ssp.adnxs.com",
        serverOverride: false,
    },
    
    XandrNativeAds: {
        placementOther1: "WAN1USEN",
        placementOther2: "WAN2GBL",
        placementPrimary: "PWP1GBL",
        placementHeader: "PWN1GBL",
        placementNonInbox: "PCWeb_NonInbox",
        placementFloatingInbox: "PCWeb_Float_Primary",
        placementFloatingOther: "PCWeb_Float_Other",
        placementOther12ndCall: "SECWAN1USEN",
        placementOther22ndCall: "SECWAN2GBL",
        placementPrimary2ndCall: "SECPWP1GBL",
        placementHeader2ndCall: "SECPWN1GBL",
        placementNonInbox2ndCall: "SECPCWeb_NonInbox",
        placementFloatingInbox2ndCall: "SECPCWeb_Float_Primary",
        placementFloatingOther2ndCall: "SECPCWeb_Float_Other",
        placementFocusInbox: "PCWeb_FocusedInbox",
        placementFocusInbox2ndCall: "SECPCWeb_FocusedInbox",
    },
    
    YammerApiResource: {
        authTargetResource: "https://www.yammer.com/",
        apiName: "YammerApi",
        baseEndpoint: "https://api.yammer.com/",
        serverOverride: false,
    },
    
    YammerResource: {
        authTargetResource: "https://www.yammer.com/",
        apiName: "Yammer",
        baseEndpoint: "https://www.yammer.com/",
        serverOverride: false,
    },
    
    UrlValidationSettings: {
        whiteListedSchemas: [],
    },
    
    Nova: {
        enableHooksTelemetery: false,
    },
};
